/**
 * Inventory page header.
 *
 * Ported from ipd/utils/IpdPageHeader (untouched — imported by ~61 files across IPD).
 * Changes:
 *   1. The actions row wraps. The original was `flexShrink: 0` with no `flexWrap`, so
 *      four buttons at 375px formed one non-shrinking row that overflowed the
 *      viewport. This was the module's highest-blast-radius responsive bug.
 *   2. `breadcrumb` — an array rendered as "Inventory Management › Goods Discard" in
 *      the eyebrow slot, so module sidebars can show where you are.
 *   3. The count badge uses the shared chip geometry instead of its own variant.
 */
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { IPD_COLORS, INV_TYPE } from '../theme';
import { invChipSx } from './StatusChip';

export default function PageHeader({
  eyebrow,
  breadcrumb,
  title,
  description,
  descriptionTrailing,
  badge,
  actions,
}) {
  const crumbs = Array.isArray(breadcrumb) ? breadcrumb.filter(Boolean) : null;
  const eyebrowText = crumbs?.length ? crumbs.join(' › ') : eyebrow;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      mb: 2.5,
      flexWrap: 'wrap',
      gap: 2,
    }}>
      <Box sx={{ minWidth: 0 }}>
        {eyebrowText && (
          <Typography sx={{
            fontSize: 12,
            color: IPD_COLORS.textSecondary,
            mb: 0.5,
            fontWeight: 500,
          }}>
            {eyebrowText}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            component="h1"
            sx={{ ...INV_TYPE.pageTitle, color: IPD_COLORS.textPrimary }}
          >
            {title}
          </Typography>
          {badge != null && (
            <Chip
              label={badge}
              size="small"
              sx={invChipSx({ color: IPD_COLORS.info.text, variant: 'soft' })}
            />
          )}
        </Box>
        {(description || descriptionTrailing) && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: badge != null ? 0.5 : 0.75,
            flexWrap: 'wrap',
          }}>
            {description && (
              <Typography sx={{ ...INV_TYPE.body, color: IPD_COLORS.textSecondary }}>
                {description}
              </Typography>
            )}
            {descriptionTrailing}
          </Box>
        )}
      </Box>
      {actions && (
        <Box sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          minWidth: 0,
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}
